import type { UseQueryOptions } from "react-query";
import { useQuery } from "react-query";
import { t } from "@/i18n-js/instance";
import type { BaseBlock } from "@/react/components/shared/BuilderUI/DataModel.types";
import type { IdArgs } from "@circle-react/api/broadcastsApi";
import { broadcastsApi } from "@circle-react/api/broadcastsApi";
import type {
  BroadcastStatusValue,
  FailureReasonValue,
} from "@circle-react/components/SettingsApp/Emails/components/Broadcasts/constants";
import type { TemplateType } from "@circle-react/components/shared/Emails/VisualEmailBuilder/useUpsertVisualEditorTemplateModal";
import type { CommunitySegmentRule } from "@circle-react/types/CommunitySegment";
import type { EmailThemeApiResponse } from "@circle-react/types/EmailTheme";
import { useToast } from "@circle-react-shared/uikit/ToastV2";

export interface EmailContentAttributes {
  body: any;
  inline_attachments: any;
  sgids_to_object_map: any;
}

export interface BroadcastContentAttributes {
  email_content: EmailContentAttributes;
  email_content_html: string;
  email_content_type: "tip_tap_email" | "visual_email_builder";
  preview_text: string;
  subject: string;
  updated_at: string;
  visual_email_builder_content?: {
    content: BaseBlock;
    inline_attachments: [
      {
        id: string;
        signed_id: string;
        filename: string;
        content_type: string;
        byte_size: number;
        url: string;
      },
    ];
    sgids_to_object_map: Record<string, unknown>;
  };
  visual_email_builder_content_html?: string;
}

export interface CommunitySegmentAttributes {
  audience_count: number;
  id: number;
  rules: CommunitySegmentRule;
}

export type ScheduleOptions = "send_now" | "schedule_for_later";

export interface BroadcastBuilderFormFields {
  broadcast_content?: BroadcastContentAttributes;
  community_segment?: CommunitySegmentAttributes;
  disable_save_email_template_prompt: boolean | null;
  email_theme?: EmailThemeApiResponse;
  id: number;
  internal_name: string;
  marketing_email_template_id: number | null;
  marketing_email_template_name: string | null;
  marketing_email_template_type?: TemplateType;
  scheduled_at: string | null;
  send_type: ScheduleOptions | "";
  status: BroadcastStatusValue;
}

interface ResponseData extends BroadcastBuilderFormFields {
  failure_reason: FailureReasonValue;
  id: number;
  status: BroadcastStatusValue;
  updated_at: string;
}

export const getBroadcastQueryKey = (id: number) => ["broadcasts", "show", id];

export const useGetBroadcast = ({
  id,
  ...queryOptions
}: IdArgs & UseQueryOptions<ResponseData, unknown>) => {
  const toastr = useToast();

  return useQuery<ResponseData>({
    queryKey: getBroadcastQueryKey(id),
    queryFn: () => broadcastsApi.show({ id }),
    onError: () => toastr.error(t("error")),
    enabled: !!id,
    ...queryOptions,
  });
};
